import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

const ERROR_MESSAGE_DISPLAY_DURATION = 10000;
const ERROR_BUTTON_REENABLE_DELAY = 1000;
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;
const PREORDER_TYPE_PROPERTY = 'نوع الطلب';
const PREORDER_TYPE_VALUE = 'طلب مسبق';
const PREORDER_ETA_PROPERTY = 'تاريخ التوفر المتوقع';
const PREORDER_POLICY_PROPERTY = 'سياسة التأخير';
const DEFAULT_DELAY_POLICY = 'قد يتأخر الشحن حسب توفر المنتج';
const INVENTORY_POLICY_DENY_MESSAGE = 'تعذر إضافة الكمية غير المتوفرة كطلب مسبق. فعّل خيار متابعة البيع عند نفاد المخزون لهذا المتغير في Shopify ثم أعد المحاولة.';
const PARTIAL_ADD_ROLLBACK_MESSAGE = 'تعذر إنشاء جزء الطلب المسبق، لذلك تمت إعادة السلة إلى حالتها السابقة. حاول مرة أخرى.';

const toInt = (value, fallback = 0) => {
  const parsed = Number.parseInt(String(value ?? ''), 10);
  return Number.isFinite(parsed) ? parsed : fallback;
};
const isTrue = (value) => value === true || value === 'true';

const getSectionIds = () => {
  const sectionIds = [];
  document.querySelectorAll('cart-items-component').forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
  });
  return sectionIds;
};

const removePreorderProperties = (formData) => {
  for (const key of Array.from(formData.keys())) {
    if (key.startsWith('properties[')) formData.delete(key);
  }
};

const applySections = (formData) => {
  const sectionIds = getSectionIds();
  if (sectionIds.length) formData.set('sections', sectionIds.join(','));
};

const buildAddFormData = (form, quantity, properties = null) => {
  const formData = new FormData(form);
  removePreorderProperties(formData);
  formData.set('quantity', String(quantity));
  if (properties) {
    formData.set(`properties[${PREORDER_TYPE_PROPERTY}]`, PREORDER_TYPE_VALUE);
    if (properties.eta) formData.set(`properties[${PREORDER_ETA_PROPERTY}]`, properties.eta);
    if (properties.delayPolicy) formData.set(`properties[${PREORDER_POLICY_PROPERTY}]`, properties.delayPolicy);
  }
  applySections(formData);
  return formData;
};

const isPreorderLineItem = (item) => item?.properties?.[PREORDER_TYPE_PROPERTY] === PREORDER_TYPE_VALUE;

export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];
  #resetTimeouts = [];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resetTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  disable() { this.refs.addToCartButton.disabled = true; }
  enable() { this.refs.addToCartButton.disabled = false; }

  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    const productForm = this.closest('product-form-component');
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      if (!validation.canAdd) return;
    }
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) this.#animateFlyToCart();
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;
    if (image) preloadImage(image);
  };

  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    const image = this.dataset.productVariantMedia;
    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = document.createElement('fly-to-cart');
    const flyToCartClass = addToCartButton.classList.contains('quick-add__button') ? 'fly-to-cart--quick' : 'fly-to-cart--main';
    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;
    document.body.appendChild(flyToCartElement);
  }

  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;
    this.#resetTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') addToCartButton.dataset.added = 'true';
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) this.#resetTimeouts.splice(index, 1);
    }, 800);
    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();
  #timeout;

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  #getVariantMap() {
    const script = this.querySelector('script[data-vitaminat-variant-preorder-map]');
    if (!script?.textContent) return {};
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.error('Invalid Vitaminat preorder variant map:', error);
      return {};
    }
  }

  #getCurrentVariantInfo(variantId) {
    const mapInfo = this.#getVariantMap()?.[String(variantId)] || {};
    const selector = this.querySelector(`.quantity-selector[data-variant-id="${variantId}"]`);
    const source = selector instanceof HTMLElement ? selector.dataset : this.dataset;
    const bundleHasPreorder = isTrue(mapInfo.bundleHasPreorder ?? source.vitaminatBundleHasPreorder);

    return {
      availableInventory: Math.max(toInt(mapInfo.availableInventory ?? source.vitaminatAvailableInventory, 0), 0),
      inventoryPolicy: String(mapInfo.inventoryPolicy ?? source.vitaminatInventoryPolicy ?? ''),
      inventoryManaged: isTrue(mapInfo.inventoryManaged ?? source.vitaminatInventoryManaged),
      preorderEligible: bundleHasPreorder || isTrue(mapInfo.preorderEligible ?? source.vitaminatPreorderEligible),
      forcePreorder: isTrue(mapInfo.forcePreorder ?? source.vitaminatForcePreorder),
      eta: String(mapInfo.eta ?? source.vitaminatPreorderEta ?? ''),
      delayPolicy: String(mapInfo.delayPolicy ?? source.vitaminatDelayPolicy ?? DEFAULT_DELAY_POLICY),
      bundleHasPreorder,
    };
  }

  #updateDatasetForVariant(variantId) {
    const info = this.#getCurrentVariantInfo(variantId);
    this.dataset.vitaminatAvailableInventory = String(info.availableInventory);
    this.dataset.vitaminatInventoryPolicy = info.inventoryPolicy;
    this.dataset.vitaminatInventoryManaged = String(info.inventoryManaged);
    this.dataset.vitaminatPreorderEligible = String(info.preorderEligible);
    this.dataset.vitaminatForcePreorder = String(info.forcePreorder);
    this.dataset.vitaminatPreorderEta = info.eta;
    this.dataset.vitaminatDelayPolicy = info.delayPolicy;
    this.dataset.vitaminatBundleHasPreorder = String(info.bundleHasPreorder);
  }

  #getRequestedQuantity(form) {
    const formData = new FormData(form);
    const quantity = toInt(formData.get('quantity'), toInt(this.dataset.quantityDefault, 1));
    return Math.max(quantity, 1);
  }

  async #fetchCart() {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Failed to fetch cart');
    return response.json();
  }

  async #postCartAdd(formData) {
    const fetchCfg = fetchConfig('javascript', { body: formData });
    const response = await fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: { ...fetchCfg.headers, Accept: 'text/html' },
    });
    const data = await response.json();
    if (!response.ok && !data.status) data.status = response.status;
    return data;
  }

  async #postCartChange(id, quantity) {
    const body = JSON.stringify({
      id,
      quantity,
      sections: getSectionIds().join(','),
      sections_url: window.location.pathname,
    });
    const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
    return response.json();
  }

  async #buildPreorderPlan(form, requestedQuantity, variantInfo) {
    const variantId = String(new FormData(form).get('id') || '');
    const cart = await this.#fetchCart();
    const existingNormalItems = cart.items?.filter((item) => String(item.variant_id) === variantId && !isPreorderLineItem(item)) || [];
    const existingNormalQuantity = existingNormalItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0);

    if (!variantInfo.preorderEligible) {
      return {
        error: null,
        variantId,
        existingNormalQuantity,
        existingNormalKey: existingNormalItems[0]?.key || '',
        parts: [{ quantity: requestedQuantity, preorder: false }],
      };
    }

    const normalCapacity = Math.max(variantInfo.availableInventory - existingNormalQuantity, 0);

    if (variantInfo.forcePreorder) {
      if (variantInfo.inventoryPolicy !== 'continue') {
        return { error: INVENTORY_POLICY_DENY_MESSAGE, variantId, existingNormalQuantity, existingNormalKey: existingNormalItems[0]?.key || '', parts: [] };
      }
      return {
        error: null,
        variantId,
        existingNormalQuantity,
        existingNormalKey: existingNormalItems[0]?.key || '',
        parts: [{ quantity: requestedQuantity, preorder: true }],
      };
    }

    if (requestedQuantity <= normalCapacity) {
      return {
        error: null,
        variantId,
        existingNormalQuantity,
        existingNormalKey: existingNormalItems[0]?.key || '',
        parts: [{ quantity: requestedQuantity, preorder: false }],
      };
    }

    if (variantInfo.inventoryPolicy !== 'continue') {
      return { error: INVENTORY_POLICY_DENY_MESSAGE, variantId, existingNormalQuantity, existingNormalKey: existingNormalItems[0]?.key || '', parts: [] };
    }

    const overflowQuantity = requestedQuantity - normalCapacity;
    return {
      error: null,
      variantId,
      existingNormalQuantity,
      existingNormalKey: existingNormalItems[0]?.key || '',
      parts: [
        ...(normalCapacity > 0 ? [{ quantity: normalCapacity, preorder: false }] : []),
        { quantity: overflowQuantity, preorder: true },
      ],
    };
  }

  async #rollbackNormalPart(plan, successfulNormalResponse) {
    let lineKey = successfulNormalResponse?.key || plan.existingNormalKey;
    if (!lineKey) {
      const cart = await this.#fetchCart();
      lineKey = cart.items?.find((item) => String(item.variant_id) === plan.variantId && !isPreorderLineItem(item))?.key || '';
    }
    if (!lineKey) return;
    await this.#postCartChange(lineKey, plan.existingNormalQuantity);
  }

  #updateCartQuantityFromData(cart) {
    const variantIdInput = this.querySelector('input[name="id"]');
    if (!variantIdInput?.value || !cart?.items) return 0;
    const cartQty = cart.items
      .filter((item) => item.variant_id.toString() === variantIdInput.value.toString())
      .reduce((sum, item) => sum + item.quantity, 0);

    const quantitySelector = this.querySelector('quantity-selector-component');
    if (quantitySelector?.setCartQuantity) quantitySelector.setCartQuantity(cartQty);
    this.#updateQuantityLabel(cartQty);
    return cartQty;
  }

  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = this.querySelector('input[name="id"]');
    if (!variantIdInput?.value) return 0;
    try {
      const cart = await this.#fetchCart();
      return this.#updateCartQuantityFromData(cart);
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return 0;
    }
  }

  #onCartUpdate = async (event) => {
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;
    const cart = event.detail?.resource;
    if (cart?.items) this.#updateCartQuantityFromData(cart);
    else await this.#fetchAndUpdateCartQuantity();
  };

  async handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    event.preventDefault();
    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = this.querySelectorAll('add-to-cart-component');
    const anyButtonDisabled = Array.from(allAddToCartContainers).some((container) => container.refs.addToCartButton?.disabled);
    if (anyButtonDisabled) return;

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    if (this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();
      if (!validation.canAdd) {
        for (const container of allAddToCartContainers) container.disable();
        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        this.#showError(errorMessage, addToCartTextError);
        setTimeout(() => { for (const container of allAddToCartContainers) container.enable(); }, ERROR_BUTTON_REENABLE_DELAY);
        return;
      }
    }

    for (const container of allAddToCartContainers) container.disable();

    const variantId = String(new FormData(form).get('id') || '');
    const requestedQuantity = this.#getRequestedQuantity(form);
    const variantInfo = this.#getCurrentVariantInfo(variantId);
    let plan;

    try {
      plan = await this.#buildPreorderPlan(form, requestedQuantity, variantInfo);
      if (plan.error) {
        this.#showError(plan.error, addToCartTextError);
        return;
      }

      let finalResponse = null;
      let successfulNormalResponse = null;

      for (const part of plan.parts) {
        if (part.quantity <= 0) continue;
        const formData = buildAddFormData(
          form,
          part.quantity,
          part.preorder ? { eta: variantInfo.eta, delayPolicy: variantInfo.delayPolicy } : null,
        );
        const response = await this.#postCartAdd(formData);

        if (response.status) {
          if (successfulNormalResponse) {
            try {
              await this.#rollbackNormalPart(plan, successfulNormalResponse);
              this.#showError(PARTIAL_ADD_ROLLBACK_MESSAGE, addToCartTextError);
            } catch (rollbackError) {
              console.error('Vitaminat preorder rollback failed:', rollbackError);
              this.#showError(response.description || response.message || 'تعذر إضافة الكمية المطلوبة بالكامل. راجع السلة قبل المتابعة.', addToCartTextError);
            }
          } else {
            this.#showError(response.description || response.message || 'تعذر إضافة الكمية المطلوبة.', addToCartTextError);
          }

          this.dispatchEvent(new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors));
          this.dispatchEvent(new CartAddEvent({}, this.id, {
            didError: true,
            source: 'product-form-component',
            itemCount: requestedQuantity,
            productId: this.dataset.productId,
          }));
          return;
        }

        if (!part.preorder) successfulNormalResponse = response;
        finalResponse = response;
      }

      if (!finalResponse) return;

      if (addToCartTextError) {
        addToCartTextError.classList.add('hidden');
        addToCartTextError.removeAttribute('aria-live');
      }

      const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
      if (anyAddToCartButton) {
        const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
        const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
        this.#setLiveRegionText(addedText);
        setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
      }

      await this.#fetchAndUpdateCartQuantity();
      this.dispatchEvent(new CartAddEvent({}, variantId, {
        source: 'product-form-component',
        itemCount: requestedQuantity,
        productId: this.dataset.productId,
        sections: finalResponse.sections,
      }));
    } catch (error) {
      console.error(error);
      this.#showError('تعذر تحديث السلة. حاول مرة أخرى.', addToCartTextError);
    } finally {
      for (const container of allAddToCartContainers) container.enable();
      cartPerformance.measureFromEvent('add:user-action', event);
    }
  }

  #showError(message, addToCartTextError = this.refs.addToCartTextError) {
    if (!addToCartTextError) return;
    addToCartTextError.classList.remove('hidden');
    const textNode = addToCartTextError.childNodes[2];
    if (textNode) textNode.textContent = message;
    else addToCartTextError.appendChild(document.createTextNode(message));
    this.#setLiveRegionText(message);
    if (this.#timeout) clearTimeout(this.#timeout);
    this.#timeout = setTimeout(() => {
      if (!addToCartTextError) return;
      addToCartTextError.classList.add('hidden');
      this.#clearLiveRegionText();
    }, ERROR_MESSAGE_DISPLAY_DURATION);
  }

  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  #setLiveRegionText(text) { this.refs.liveRegion.textContent = text; }
  #clearLiveRegionText() { this.refs.liveRegion.textContent = ''; }

  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) morph(currentElement, newElement);
    else if (currentElement && !newElement) currentElement.remove();
    else if (!currentElement && newElement && insertReferenceElement) insertReferenceElement.insertAdjacentElement('beforebegin', newElement.cloneNode(true));
  }

  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) this.dataset.productId = event.detail.data.newProduct.id;
    else if (event.detail.data.productId !== this.dataset.productId) return;

    const { variantId } = this.refs;
    variantId.value = event.detail.resource?.id ?? '';
    if (variantId.value) this.#updateDatasetForVariant(variantId.value);

    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;
    if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

    if (event.detail.resource == null || event.detail.resource.available == false) currentAddToCartButtonContainer.disable();
    else currentAddToCartButtonContainer.enable();

    const newAddToCartButton = event.detail.data.html.querySelector('product-form-component [ref="addToCartButton"]');
    if (newAddToCartButton && currentAddToCartButton) morph(currentAddToCartButton, newAddToCartButton);

    if (acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      else acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }

    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) this.refs.addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }

    const { quantityRules, pricePerItem, quantitySelector, productFormButtons, quantityLabel, quantitySelectorWrapper } = this.refs;
    const newQuantityInput = event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]');
    if (quantitySelector?.updateConstraints && newQuantityInput) quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      const currentQuantityValue = quantitySelector.getValue?.();
      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');
      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);
        const newQuantityInputElement = event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]');
        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          this.refs.quantitySelector.setValue(currentQuantityValue);
          this.refs.quantitySelector.updateConstraints(newQuantityInputElement.min, newQuantityInputElement.max || null, newQuantityInputElement.step);
        }
      }
    } else {
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];
      for (const [selector, currentElement, fallback] of morphTargets) this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
    }

    this.#morphOrUpdateElement(this.refs.volumePricing, event.detail.data.html.querySelector('volume-pricing'), this.refs.productFormButtons);
    await this.#fetchAndUpdateCartQuantity();
  };

  #onVariantSelected = () => { this.refs.addToCartButtonContainer?.disable(); };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
